import { computed } from 'vue'
import { useNotificationStore } from './store/notifications'
import { useFeatureFlags } from './composables/useFeatureFlags'

export { useFeatureFlags } from './composables/useFeatureFlags'
export { useSnackbar } from './composables/useSnackbar'
export { useRealTimeNotifications } from './composables/useRealTimeNotifications'
export { useMercureConnection } from './composables/useMercureConnection'
export { useFileValidation } from './composables/useFileValidation'
export { useTelegramBinding } from './composables/useTelegramBinding'
export { useErrorTracking } from './composables/useErrorTracking'

/** Shell-компоненты platform / feature UI - без reach-in в common/components. */
export { default as AuthPromptModal } from './components/AuthPromptModal.vue'
export { default as NotificationCenter } from './components/NotificationCenter.vue'
export { default as CallbackRequestModal } from './components/CallbackRequestModal.vue'
export { default as TelegramBinding } from './components/TelegramBinding.vue'

/** SEO helpers for app bootstrap and catalog product pages. */
export { applySeo, setupRouterSeo } from './seo/seo'
export { getGeneratedProductSeoMetadata } from './seo/product-metadata'

/** Static/marketing routes owned by platform module. */
export { default as HomeView } from './views/HomeView.vue'
export { default as AboutView } from './views/AboutView.vue'
export { default as ContactsView } from './views/ContactsView.vue'
export { default as DeliveryView } from './views/DeliveryView.vue'
export { default as PrivacyView } from './views/PrivacyView.vue'
export { default as TermsView } from './views/TermsView.vue'
export { default as NotFoundView } from './views/errors/NotFoundView.vue'

/**
 * Narrow realtime subscription adapter for feature modules (task 11): owner
 * modules subscribe to their own topics via `subscribe(eventType, cb)`
 * without reaching into `common/composables/useMercure` or the Mercure
 * client/config internals. Connection lifecycle stays owned by
 * `useMercureConnection`/app shell.
 */
export { useMercure } from './composables/useMercure'
export type {
  MercureEvent,
  UnsubscribeFunction,
  Notification,
  NotificationType,
} from './services/websocket/types'

/** Platform Mercure token transport (compat for procurement re-export). */
export { realtimeApi } from './api/realtime.api'
export type { MercureTokenResponse } from './api/realtime.api'

export function useAppNotifications() {
  const notificationStore = useNotificationStore()

  return {
    notifications: computed(() => notificationStore.notifications),
    unreadCount: computed(() => notificationStore.unreadCount),
    showSuccess: (
      ...args: Parameters<typeof notificationStore.showSuccess>
    ) => notificationStore.showSuccess(...args),
    showError: (...args: Parameters<typeof notificationStore.showError>) =>
      notificationStore.showError(...args),
    showWarning: (...args: Parameters<typeof notificationStore.showWarning>) =>
      notificationStore.showWarning(...args),
    showInfo: (...args: Parameters<typeof notificationStore.showInfo>) =>
      notificationStore.showInfo(...args),
    removeNotification: (id: string) => notificationStore.removeNotification(id),
  }
}

/** Compat alias - прежний `useNotification()` из composables/notification. */
export const useNotification = useAppNotifications

export async function ensureFeatureFlagsReady() {
  const featureFlags = useFeatureFlags()
  await featureFlags.initializeFlags()
  return featureFlags
}

export function isFeatureEnabled(featureKey: string): boolean {
  return useFeatureFlags().isEnabled(featureKey)
}
